import { useEffect, useRef, useState } from "react";
import { CheckCircle2, ChevronDown, Send, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { INQUIRY_ENDPOINT, NEED_OPTIONS } from "./config";
import { getAttribution, trackGaEvent } from "./analytics";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const initialValues = { name: "", email: "", need: "", idea: "" };

const fieldClass =
  "w-full rounded-xl border border-white/10 bg-ink/70 px-4 py-3 text-sm text-paper placeholder:text-paper/35 transition-[border-color,box-shadow] duration-200 focus:border-lilac/60 focus:outline-none focus:ring-2 focus:ring-lilac/30";
const labelClass = "mb-1.5 block text-sm font-bold text-paper/85";
const errorClass = "mt-1.5 text-xs font-semibold text-red-300";

export const InquiryDialog = ({ open, onOpenChange, prefill }) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | error | success
  const refs = { name: useRef(null), email: useRef(null), need: useRef(null), idea: useRef(null) };

  useEffect(() => {
    if (open && prefill) {
      setValues((v) => ({
        ...v,
        ...(prefill.need ? { need: prefill.need } : {}),
        ...(prefill.idea ? { idea: prefill.idea } : {}),
        ...(prefill.name ? { name: prefill.name } : {}),
        ...(prefill.email ? { email: prefill.email } : {}),
      }));
    }
  }, [open, prefill]);

  const set = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    setErrors((err) => ({ ...err, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = "Please tell us your name.";
    if (!values.email.trim()) next.email = "Please enter your email address.";
    else if (!EMAIL_RE.test(values.email.trim())) next.email = "That email doesn't look right — mind checking it?";
    if (!values.need) next.need = "Please choose what you need.";
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    const firstError = Object.keys(next).find((k) => next[k]);
    if (firstError) {
      refs[firstError].current?.focus();
      return;
    }
    setStatus("sending");
    try {
      if (!INQUIRY_ENDPOINT) {
        // No submission destination configured yet — never fake success.
        await new Promise((r) => setTimeout(r, 600));
        throw new Error("delivery-not-configured");
      }
      const res = await fetch(INQUIRY_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, attribution: getAttribution() }),
      });
      if (!res.ok) throw new Error("send-failed");
      setStatus("success");
      trackGaEvent("generate_lead", { method: "inquiry_form" });
    } catch {
      setStatus("error"); // entered values are preserved
    }
  };

  const handleOpenChange = (next) => {
    onOpenChange(next);
    if (!next && status === "success") {
      setValues(initialValues);
      setErrors({});
      setStatus("idle");
    } else if (!next) {
      setStatus("idle");
      setErrors({});
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="inquiry-dialog"
        className="rounded-2xl border-white/10 bg-card text-paper sm:max-w-md [&>button]:text-paper/60 [&>button]:transition-colors [&>button]:hover:text-paper"
      >
        {status === "success" ? (
          <div className="flex flex-col items-center py-6 text-center">
            <CheckCircle2 className="h-12 w-12 text-lilac" aria-hidden="true" />
            <DialogHeader className="mt-4">
              <DialogTitle className="text-center font-display text-2xl font-extrabold text-paper">
                Inquiry sent.
              </DialogTitle>
              <DialogDescription className="text-center text-paper/60">
                Thank you — we've received your inquiry and will reply within one business day with next steps.
                A confirmation is on its way to your inbox.
              </DialogDescription>
            </DialogHeader>
            <button
              type="button"
              data-testid="inquiry-success-close-button"
              onClick={() => handleOpenChange(false)}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-lilac px-7 py-3 text-sm font-bold text-ink transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-lilac-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-card"
            >
              <X className="h-4 w-4" aria-hidden="true" />
              Close
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold text-paper">
                Tell us what you'd like to build.
              </DialogTitle>
              <DialogDescription className="text-paper/60">
                Share a few details so we can understand your project.
              </DialogDescription>
            </DialogHeader>

            <div
              data-testid="inquiry-next-steps"
              className="mt-4 rounded-xl border border-white/[0.08] bg-ink/50 p-3.5 text-xs leading-relaxed text-paper/55"
            >
              What happens next: we review your note and reply within one business day with a scoped
              proposal — deliverables, timeline, and exact pricing. No payment is taken at this step.
            </div>

            {status === "error" && (
              <div
                role="alert"
                data-testid="inquiry-delivery-error"
                className="mt-4 rounded-xl border border-amber-300/30 bg-amber-400/10 p-3.5 text-sm text-amber-100"
              >
                Something went wrong sending your inquiry. Your details are kept below — please try again.
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="mt-5 space-y-4">
              <div>
                <label htmlFor="inquiry-name" className={labelClass}>
                  Name
                </label>
                <input
                  id="inquiry-name"
                  data-testid="inquiry-name-input"
                  ref={refs.name}
                  type="text"
                  autoComplete="name"
                  value={values.name}
                  onChange={set("name")}
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? "inquiry-name-error" : undefined}
                  className={fieldClass}
                  placeholder="Your name"
                />
                {errors.name && (
                  <p id="inquiry-name-error" role="alert" data-testid="inquiry-name-error" className={errorClass}>
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="inquiry-email" className={labelClass}>
                  Email
                </label>
                <input
                  id="inquiry-email"
                  data-testid="inquiry-email-input"
                  ref={refs.email}
                  type="email"
                  autoComplete="email"
                  value={values.email}
                  onChange={set("email")}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "inquiry-email-error" : undefined}
                  className={fieldClass}
                  placeholder="you@example.com"
                />
                {errors.email && (
                  <p id="inquiry-email-error" role="alert" data-testid="inquiry-email-error" className={errorClass}>
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="inquiry-need" className={labelClass}>
                  What do you need?
                </label>
                <div className="relative">
                  <select
                    id="inquiry-need"
                    data-testid="inquiry-need-select"
                    ref={refs.need}
                    value={values.need}
                    onChange={set("need")}
                    aria-invalid={Boolean(errors.need)}
                    aria-describedby={errors.need ? "inquiry-need-error" : undefined}
                    className={`${fieldClass} appearance-none pr-10 ${values.need ? "" : "text-paper/35"}`}
                  >
                    <option value="" disabled>
                      Choose one
                    </option>
                    {NEED_OPTIONS.map((opt) => (
                      <option key={opt} value={opt} className="bg-ink text-paper">
                        {opt}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-paper/40"
                    aria-hidden="true"
                  />
                </div>
                {errors.need && (
                  <p id="inquiry-need-error" role="alert" data-testid="inquiry-need-error" className={errorClass}>
                    {errors.need}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="inquiry-idea" className={labelClass}>
                  A little about your idea <span className="font-normal text-paper/40">(optional)</span>
                </label>
                <textarea
                  id="inquiry-idea"
                  data-testid="inquiry-idea-textarea"
                  ref={refs.idea}
                  rows={3}
                  value={values.idea}
                  onChange={set("idea")}
                  className={`${fieldClass} resize-none`}
                  placeholder="A sentence or two is plenty."
                />
              </div>

              <button
                type="submit"
                data-testid="inquiry-submit-button"
                disabled={status === "sending"}
                className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-lilac px-7 py-3.5 text-sm font-bold text-ink transition-[transform,box-shadow,background-color,opacity] duration-200 ease-out hover:-translate-y-0.5 hover:bg-lilac-bright hover:shadow-[0_16px_36px_-12px_rgba(184,167,232,0.55)] active:translate-y-0 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span>{status === "sending" ? "Sending…" : "Send project inquiry"}</span>
                <Send
                  className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
